import {
  BASE_PRODUCTS_PRICING_QUERY_PRICING_SERVICE,
  BASE_PRODUCTS_RELATED_PRODUCTS_QUERY,
} from '~/builder/queries/grid';

import { getProductGqlQueryParameters } from './getProductGridQueryParameters';

/* according to Matt Mccabe from the API team, this particular endpoint treats code 3930 and 3925 as vip,
 all other codes are treated as lead, see full doc of the codes: https://confluence.techstyle.net/display/API/Status+Codes.+ALL+OF+THEM */
export const MEMBERSHIP_STATUS = {
  VIP: 3930,
  PROSPECTIVE: 3926,
};

export const formatProductPricingData = ({ rawPricingData }) => {
  if (!rawPricingData) {
    return {};
  }
  return rawPricingData?.reduce(
    (acc, productPricing) => ({
      ...acc,
      [productPricing.masterProductId]: {
        pricing: productPricing,
        retailUnitPrice: productPricing.retail,
        defaultUnitPrice: productPricing.final,
        outletUnitPrice: productPricing.outlet,
        promos: productPricing.finalDetails,
        promoCode: productPricing.finalDetails?.promoCode,
      },
    }),
    {}
  );
};

export const getGqlPricingMap = async ({
  products,
  apolloClient,
  isVip = false,
}) => {
  try {
    const productPricingVariables = {
      masterProductIds: products.map((product) => product.masterProductId),
      membershipStatus: isVip
        ? MEMBERSHIP_STATUS.VIP
        : MEMBERSHIP_STATUS.PROSPECTIVE,
    };

    const { data } = await apolloClient.query({
      context: {
        clientName: 'supergraph',
        headers: {
          'x-datasource-version': 'pricingService',
        },
      },
      query: BASE_PRODUCTS_PRICING_QUERY_PRICING_SERVICE,
      variables: productPricingVariables,
      errorPolicy: 'all',
    });

    const rawPricingData = data?.productPricing;

    return formatProductPricingData({
      rawPricingData,
    });
  } catch (e) {
    return {};
  }
};

export const formatProductRelatedProductsListing = (
  productRelatedProductsListing
) => {
  if (!productRelatedProductsListing) {
    return {};
  }
  return productRelatedProductsListing?.products?.reduce((acc, product) => {
    // index the swatches by productId
    const swatchesMap = product.swatches.reduce((swAcc, swatch) => {
      swAcc[swatch.productId] = swatch.url;
      return swAcc;
    }, {});

    const updatedRelatedProducts = product.relatedProducts
      .map((relatedProduct) => {
        const productImages = relatedProduct.images;
        const tagIdList = relatedProduct.tags.map((tag) => tag.id);

        const newRelatedProduct = {
          ...relatedProduct,
          swatchUrl: swatchesMap[relatedProduct.masterProductId],
          productImages,
          tagIdList,
        };

        // don't needs tags array anymore after getting the tagIdList
        delete newRelatedProduct.tags;

        return newRelatedProduct;
      })
      .sort((a, b) => b.isSelf - a.isSelf);

    acc[product.masterProductId] = {
      relatedProductIdObjectList: updatedRelatedProducts,
      componentProductIdObjectList: product.componentProductIdObject,
    };

    return acc;
  }, {});
};

export const getGqlRelatedProductMap = async ({
  products,
  apolloClient,
  shouldShowWarehouseProducts,
  excludeTagIds,
}) => {
  try {
    const relatedProductsVariables = getProductGqlQueryParameters({
      productListing: products,
      shouldShowWarehouseProducts,
      excludeTagIds,
    });

    const {
      data: { productListing: productRelatedProductsListing },
    } = await apolloClient.query({
      context: { clientName: 'supergraph' },
      query: BASE_PRODUCTS_RELATED_PRODUCTS_QUERY,
      variables: relatedProductsVariables,
    });
    return formatProductRelatedProductsListing(productRelatedProductsListing);
  } catch (e) {
    return {};
  }
};
